// SessionLogger - records every game session for later balance analysis.
//
// Each session is a JSON object containing:
//   - id:         unique string
//   - commit:     current build commit (from version.json, or 'unknown')
//   - startedAt:  ISO timestamp
//   - endedAt:    ISO timestamp (set on endSession)
//   - events:     chronological list of lightweight event records
//   - summary:    aggregated counters updated by hooks during play
//
// startSession also sends a small receipt (see buildReceipt) so runs
// abandoned before endSession still show up server-side.
//
// Storage: localStorage under STORAGE_KEY, up to MAX_SESSIONS kept
// (oldest trimmed). Safe in Node (tests) and in browsers without storage
// thanks to the _readStore / _writeStore wrappers.
//
// Events are tiny on purpose — logging a full combat state every frame
// would blow out storage. We only log state transitions and terminal
// events (kills, picks, damage, death). Running counters in `summary`
// give us aggregated metrics without storing per-tick detail.
//
// Constructor options:
//   commit  - build commit string embedded in every session.
//   clock   - optional () => number. When set, replaces Date.now() for
//             event timestamps, _startMs, and durationMs. The simulator
//             uses this to drive a logical (step-based) clock so two
//             seeded runs produce byte-identical JSON.
//
// startSession(meta): if `meta.id` is present it replaces the generated
// session id. The simulator uses this so a session's id is a
// deterministic function of its seed, which in turn keeps the exported
// JSON diffable across runs.

// Bump when the session shape changes; mirrors the V1..Vn eras in
// corovans-dashboard/docs/session-schema.md. Lets ETL pick the parser
// without guessing from field presence. V10 changed no session field: it
// marks builds that announce each session with a start receipt. V11: super
// strike + horse — `super_used`, `horse_damaged`, `horse_lost` events,
// guard_killed source 'super', pickup kind 'mount', summary.supersUsed /
// horseDamage. V12: meta.platform (+ receipt.platform) — where the game
// runs, see detectPlatform.
export const SCHEMA_VERSION = 12;

const STORAGE_KEY = 'korovany.sessions';
const PLAYER_ID_KEY = 'korovany.playerId';
const MAX_SESSIONS = 100;

// Stable per-browser player id. Generated on first session, persisted in
// localStorage, attached to every uploaded session as `meta.playerId`.
// Used by analyze.js to compute unique players, runs/player, retention.
// Clearing localStorage / using a different browser = new id.
function getOrCreatePlayerId() {
  if (typeof localStorage === 'undefined') return null;
  try {
    let id = localStorage.getItem(PLAYER_ID_KEY);
    if (!id) {
      id = genId();
      localStorage.setItem(PLAYER_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return null;
  }
}

const PLAYER_NAME_STORAGE_KEY = 'korovany.playerName';
function getPlayerNameFromStorage() {
  if (typeof localStorage === 'undefined') return '';
  try {
    const v = localStorage.getItem(PLAYER_NAME_STORAGE_KEY);
    return v && v.trim() ? v.trim() : '';
  } catch (e) {
    return '';
  }
}

// --- Telemetry upload ------------------------------------------------
//
// Sessions are POSTed to TELEMETRY_URL — production endpoint is the
// d.123-managed backend at tlm.corovans.d.123automate.it (token-gated,
// writes to /var/lib/corovans-tlm/sessions on disk). Fire-and-forget: a
// failed upload never blocks gameplay and never throws — the session is
// still kept in localStorage as the primary store.
//
// Set TELEMETRY_URL to null to disable (headless/offline). The token is
// embedded so any browser session can post; it's a "weak secret" that
// gates only random spam, not malicious clients.
// Override at runtime from the browser console:
//   window.KOROVANY_TELEMETRY_URL = 'http://localhost:12000/sessions'
//   window.KOROVANY_TELEMETRY_TOKEN = '...'
// (read lazily inside uploadSession so a console override takes effect
// without reload).
// The Russian mirror (korovany.123automate.it, a reverse proxy on a Russian
// VPS — the main host is unreachable from Russia without a VPN) posts to its
// own proxied endpoint, which forwards to the same backend.
const MIRROR_HOST = 'korovany.123automate.it';
// Where this copy of the game runs: 'ios' (App Store wrapper, corovans://),
// 'android' (Play / sideload APK, WebViewAssetLoader host), 'web-mirror'
// (the RU mirror), 'web' (anything else served over http). null headless.
const ANDROID_ASSET_HOST = 'appassets.androidplatform.net';
export function detectPlatform(loc = typeof location !== 'undefined' ? location : null) {
  if (!loc) return null;
  if (loc.protocol === 'corovans:') return 'ios';
  if (loc.hostname === ANDROID_ASSET_HOST) return 'android';
  if (loc.hostname === MIRROR_HOST) return 'web-mirror';
  return 'web';
}

const TELEMETRY_URL = typeof location !== 'undefined' && location.hostname === MIRROR_HOST
  ? 'https://tlm.korovany.123automate.it/sessions'
  : 'https://tlm.corovans.d.123automate.it/sessions';
const TELEMETRY_TOKEN = '891c6f69d778d5b9f18f5fcd17b9148cd3b1bbd70901d32701c588baa6378bae';

const OPT_OUT_KEY = 'korovany.optOutTelemetry';

export function isTelemetryOptedOut() {
  if (typeof localStorage === 'undefined') return false;
  try { return localStorage.getItem(OPT_OUT_KEY) === '1'; } catch (e) { return false; }
}

export function setTelemetryOptOut(enabled) {
  if (typeof localStorage === 'undefined') return;
  try {
    if (enabled) localStorage.setItem(OPT_OUT_KEY, '1');
    else localStorage.removeItem(OPT_OUT_KEY);
  } catch (e) {}
}

function resolveTelemetryUrl() {
  if (typeof globalThis !== 'undefined' && globalThis.KOROVANY_TELEMETRY_URL !== undefined) {
    return globalThis.KOROVANY_TELEMETRY_URL;
  }
  return TELEMETRY_URL;
}

function resolveTelemetryToken() {
  if (typeof globalThis !== 'undefined' && globalThis.KOROVANY_TELEMETRY_TOKEN !== undefined) {
    return globalThis.KOROVANY_TELEMETRY_TOKEN;
  }
  return TELEMETRY_TOKEN;
}

// POST one session object to the telemetry server. Never throws, never
// awaits — callers can ignore the returned promise.
// Fetch the top-N leaderboard entries from the telemetry server. Returns
// an array of `{name, score, wave, endedAt}` (server already sorts by
// score desc). Resolves to [] on any failure — callers should treat the
// leaderboard as best-effort decoration, not gameplay state.
export function fetchLeaderboard(limit = 10) {
  if (typeof window === 'undefined') return Promise.resolve([]);
  if (typeof fetch !== 'function') return Promise.resolve([]);
  const baseUrl = resolveTelemetryUrl();
  if (!baseUrl) return Promise.resolve([]);
  // Replace the trailing /sessions with /leaderboard so we hit the same
  // origin without a second config knob.
  const lbUrl = baseUrl.replace(/\/sessions(\?.*)?$/, '/leaderboard') + `?limit=${limit}`;
  return fetch(lbUrl, { method: 'GET' })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => (data && Array.isArray(data.entries) ? data.entries : []))
    .catch(() => []);
}

// Shared POST for sessions and receipts. Never throws; resolves to
// true/false. Only posts from real browser sessions — never from Node
// (tests, headless sim). Node 18+ has global fetch, so without the
// window guard a test run would silently pollute the real telemetry sink.
function postTelemetry(url, payload) {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (isTelemetryOptedOut()) return Promise.resolve(false);
  if (!url) return Promise.resolve(false);
  if (typeof fetch !== 'function') return Promise.resolve(false);
  const headers = {
    'Content-Type': 'application/json',
    // ngrok-free serves an HTML interstitial to browsers on first hit;
    // this header skips it. Harmless when the tunnel isn't ngrok.
    'ngrok-skip-browser-warning': 'any',
  };
  const token = resolveTelemetryToken();
  if (token) headers['X-Telemetry-Token'] = token;
  let body;
  try {
    body = JSON.stringify(payload);
  } catch (e) {
    return Promise.resolve(false);
  }
  return fetch(url, { method: 'POST', headers, body })
    .then((r) => r && r.ok)
    .catch(() => false);
}

export function uploadSession(session) {
  if (!session) return Promise.resolve(false);
  // No `keepalive: true`: that flag caps body size at 64 KiB across all
  // major browsers, which silently dropped sessions past wave ~10-11
  // (payload grows ~20 KB/wave; at wave 12+ ≈ 85-110 KB). GameOver keeps
  // the tab alive long enough for a normal POST to land. Tab-close
  // survival regresses slightly here — a follow-up `sendBeacon` on
  // `pagehide` would close that gap.
  return postTelemetry(resolveTelemetryUrl(), session);
}

// --- Session receipt -------------------------------------------------
//
// A full session is uploaded only at endSession (death / end), so a run
// whose tab is closed mid-game never reaches the server. The receipt is a
// tiny "this session started" note POSTed from startSession; the server
// stores it apart from full sessions (receipts/<id>.json), and a receipt
// with no matching sessions/<id>.json is an abandoned run.
//
// Endpoint: `<sessions url>/receipt` — under the same `/sessions` route,
// so the nginx proxy and the RU mirror need no changes. An older server
// answers 404, which is swallowed like any other failure.

// Receipt payload for a session, or null when the session must not be
// announced (sim / AI demo runs — same rule as the leaderboard).
export function buildReceipt(session) {
  if (!session) return null;
  const meta = session.meta || {};
  if (meta.sim || meta.ai) return null;
  const build = meta.build || {};
  return {
    kind: 'receipt',
    id: session.id,
    schemaVersion: session.schemaVersion,
    startedAt: session.startedAt,
    playerId: meta.playerId || null,
    version: build.version || null,
    commit: session.commit,
    firstSession: !!meta.firstSession,
    platform: meta.platform || null,
  };
}

const RECEIPT_BASE_RE = /\/sessions(\?.*)?$/;

export function uploadReceipt(receipt) {
  if (!receipt) return Promise.resolve(false);
  // A console override that doesn't end in /sessions gets no receipts —
  // posting one to a plain sessions sink would store it as a session.
  const base = resolveTelemetryUrl();
  if (!base || !RECEIPT_BASE_RE.test(base)) return Promise.resolve(false);
  // No `keepalive`: it needs a CORS preflight here (custom headers), which
  // not every browser allows for keepalive requests — the fetch would just
  // fail. The game has only just started, so the tab is still open.
  return postTelemetry(base.replace(RECEIPT_BASE_RE, '/sessions/receipt$1'), receipt);
}

// POST every session currently in localStorage. Used by the U menu
// hotkey to backfill the server after offline play. Returns a promise
// resolving to { sent, failed }.
export function uploadAllLocal() {
  const sessions = safeRead();
  if (!sessions.length) return Promise.resolve({ sent: 0, failed: 0 });
  return Promise.all(sessions.map(uploadSession)).then((results) => {
    const sent = results.filter(Boolean).length;
    return { sent, failed: results.length - sent };
  });
}

function nowIso() {
  return new Date().toISOString();
}

function genId() {
  const rand = Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0');
  return `${Date.now().toString(36)}-${rand}`;
}

function safeRead() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function safeWrite(sessions) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions));
    return true;
  } catch (e) {
    return false;
  }
}

export function loadAllSessions() {
  return safeRead();
}

export function clearAllSessions() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (e) {
    return false;
  }
}

// Count persisted sessions without parsing them all (approximate fallback
// to full parse if needed). Used by the main menu.
export function countSessions() {
  return safeRead().length;
}

// Player state at wave start (wave_start.player): HP is otherwise logged
// only on hits, so without it HP after heals and card stats are unknown.
export function playerSnapshot(player) {
  if (!player) return null;
  const round2 = (v) => Math.round(v * 100) / 100;
  return {
    hp: player.hp,
    maxHp: player.maxHp,
    regenPerSec: round2(player.regenPerSec),
    lifestealPct: round2(player.lifestealPct),
    fortifyHealPerKill: round2(player.fortifyHealPerKill),
    thornsPct: round2(player.thornsPct),
    damage: round2(player.damage),
    attackRange: round2(player.attackRange),
    attackCooldown: round2(player.attackCooldown),
    speed: round2(player.speed),
    critChance: round2(player.critChance),
    gold: player.gold,
  };
}

// Guard head-count by archetype, e.g. { basic: 6, archer: 2 }.
export function countGuardsByType(guards) {
  const counts = {};
  for (const g of guards) counts[g.type] = (counts[g.type] || 0) + 1;
  return counts;
}

export class SessionLogger {
  constructor(options = {}) {
    this.commit = options.commit || 'unknown';
    this.session = null;
    this._startMs = 0;
    // Optional logical clock for deterministic sims. When set, replaces
    // Date.now() for event timestamps + durationMs. Live game leaves it
    // null and gets wall-clock behavior.
    this._clock = typeof options.clock === 'function' ? options.clock : null;
    // Session id whose receipt was already sent (see _sendReceipt).
    this._receiptSentFor = null;
  }

  _nowMs() {
    return this._clock ? this._clock() : Date.now();
  }

  setCommit(commit) {
    this.commit = commit || 'unknown';
  }

  // Begin a new session. Discards any in-progress session without persisting
  // it — intended for fresh runs from the main menu. Pass `meta.id` to
  // force a specific session id (used by the simulator for reproducibility).
  startSession(meta = {}) {
    this._startMs = this._nowMs();
    const { id: forcedId, ...metaRest } = meta;
    // Auto-attach playerId + playerName from localStorage for real
    // player sessions. Sim runs (`sim: true`) skip this — we don't want
    // simulator output to depend on whatever localStorage stub the test
    // harness installed (would break seeded reproducibility).
    if (!metaRest.sim) {
      if (metaRest.playerId === undefined) {
        const playerId = getOrCreatePlayerId();
        if (playerId) metaRest.playerId = playerId;
      }
      if (metaRest.playerName === undefined) {
        const name = getPlayerNameFromStorage();
        if (name) metaRest.playerName = name;
      }
      if (metaRest.platform === undefined) {
        const platform = detectPlatform();
        if (platform) metaRest.platform = platform;
      }
    }
    this.session = {
      id: forcedId || genId(),
      schemaVersion: SCHEMA_VERSION,
      commit: this.commit,
      startedAt: nowIso(),
      endedAt: null,
      meta: { ...metaRest },
      events: [],
      summary: {
        waveReached: 0,
        wavesCompleted: 0,
        guardsKilled: 0,
        guardsKilledByType: { basic: 0, armored: 0, archer: 0, lancer: 0, bombardier: 0 },
        guardsKilledBySource: { melee: 0, thorns: 0, super: 0 },
        caravansRobbed: 0,
        caravansRobbedByType: { donkey: 0, wagon: 0, royal: 0 },
        pickupsCollected: 0,
        pickupsByKind: { heal: 0, coin: 0, buff: 0, jackpot: 0, mount: 0 },
        bossesDefeated: 0,
        damageDealt: 0,
        damageReflected: 0,
        damageTaken: 0,
        goldEarned: 0,
        goldSpent: 0,
        dashesUsed: 0,
        supersUsed: 0,
        horseDamage: 0,
        attacks: 0,
        rerolls: 0,
        cardsPicked: [],
        flawlessWaves: 0,
        died: false,
        finalScore: 0,
        durationMs: 0,
      },
    };
    this._log('session_start', metaRest);
    this._sendReceipt();
  }

  // Fire-and-forget receipt for the session just started. Once per
  // session id; a no-op for sim/AI runs and outside the browser.
  _sendReceipt() {
    const id = this.session && this.session.id;
    if (!id || this._receiptSentFor === id) return;
    const receipt = buildReceipt(this.session);
    if (!receipt) return;
    this._receiptSentFor = id;
    uploadReceipt(receipt);
  }

  _log(type, data = {}) {
    if (!this.session) return;
    const t = this._nowMs() - this._startMs;
    // Strip any `type` key from the payload so a future caller passing
    // e.g. `{ type: 'basic' }` can't clobber the event's own discriminator.
    // Previous bug: logGuardKilled / logCaravanRobbed spread `{ type, ... }`
    // into this array and every kill event silently became `type: 'basic'`.
    const { type: _ignored, ...safeData } = data;
    this.session.events.push({ t, type, ...safeData });
  }

  // --- Event hooks (called from game.js) -------------------------------
  //
  // Each hook updates the summary counters and appends a lightweight event
  // record. Keep these small — fields are flat so a CSV dump is trivial.

  logWaveStart(wave, info = {}) {
    if (!this.session) return;
    this.session.summary.waveReached = Math.max(this.session.summary.waveReached, wave);
    this._log('wave_start', { wave, ...info });
  }

  logWaveEnd(wave, info = {}) {
    if (!this.session) return;
    this.session.summary.wavesCompleted = Math.max(this.session.summary.wavesCompleted, wave);
    this._log('wave_end', { wave, ...info });
  }

  logGuardKilled(type, wave, source = 'melee', hasFiredFire = false) {
    if (!this.session) return;
    this.session.summary.guardsKilled++;
    const byType = this.session.summary.guardsKilledByType;
    if (byType[type] !== undefined) byType[type]++;
    const bySource = this.session.summary.guardsKilledBySource;
    if (bySource[source] !== undefined) bySource[source]++;
    // Use `guardType` rather than `type` so the spread inside _log doesn't
    // clobber the event's own `type: 'guard_killed'` field.
    // `hasFiredFire` distinguishes "burned" archers from cold ones (fire is
    // a per-shot dice, not a subtype — see caravan.js attack()).
    this._log('guard_killed', { guardType: type, wave, source, hasFiredFire });
  }

  logDamageReflected(amount) {
    if (!this.session) return;
    this.session.summary.damageReflected += amount;
  }

  // A guard-drop pickup ("ништяк") was collected. `id` is the pickup type
  // (medkit/purse/rage/frenzy/guard/spice/horse), `kind` its effect class
  // (heal/coin/buff/jackpot/mount). Gold from coin/jackpot pickups is also routed
  // through logGoldEarned by the caller, so it still lands in goldEarned.
  logPickup(id, kind, wave) {
    if (!this.session) return;
    this.session.summary.pickupsCollected++;
    const byKind = this.session.summary.pickupsByKind;
    if (byKind[kind] !== undefined) byKind[kind]++;
    this._log('pickup_collected', { id, kind, wave });
  }

  logCaravanRobbed(type, value, wave, isBoss = false) {
    if (!this.session) return;
    this.session.summary.caravansRobbed++;
    const byType = this.session.summary.caravansRobbedByType;
    if (byType[type] !== undefined) byType[type]++;
    if (isBoss) this.session.summary.bossesDefeated++;
    // Rename `type` → `caravanType` for the same reason as logGuardKilled.
    this._log('caravan_robbed', { caravanType: type, value, wave, boss: isBoss });
  }

  logDamageDealt(amount) {
    if (!this.session) return;
    this.session.summary.damageDealt += amount;
    // Not stored as a per-hit event — too noisy. Only summary.
  }

  logPlayerDamaged(amount, source, wave, hpAfter) {
    if (!this.session) return;
    this.session.summary.damageTaken += amount;
    this._log('player_damaged', { amount, source, wave, hpAfter });
  }

  logGoldEarned(amount) {
    if (!this.session) return;
    this.session.summary.goldEarned += amount;
  }

  logGoldSpent(amount) {
    if (!this.session) return;
    this.session.summary.goldSpent += amount;
  }

  logAttack() {
    if (!this.session) return;
    this.session.summary.attacks++;
  }

  logDash(wave) {
    if (!this.session) return;
    this.session.summary.dashesUsed++;
    this._log('dash', { wave });
  }

  // Super strike fired: how many targets the blast hit and how many guards
  // it killed (the kills also log as guard_killed with source 'super').
  logSuper(wave, hits, kills) {
    if (!this.session) return;
    this.session.summary.supersUsed++;
    this._log('super_used', { wave, hits, kills });
  }

  // A hit soaked up by the horse (same sources as player_damaged). Kept out
  // of damageTaken, which stays the robber's own HP loss.
  logHorseDamaged(amount, source, wave, hpAfter) {
    if (!this.session) return;
    this.session.summary.horseDamage += amount;
    this._log('horse_damaged', { amount, source, wave, hpAfter });
  }

  logHorseLost(wave) {
    if (!this.session) return;
    this._log('horse_lost', { wave });
  }

  logRelicUnlocked(id, wave) {
    if (!this.session) return;
    this._log('relic_unlocked', { id, wave });
  }

  logCardPicked(cardId, rarity, mode, cost, wave) {
    if (!this.session) return;
    this.session.summary.cardsPicked.push({ id: cardId, rarity, mode, cost, wave });
    this._log('card_picked', { id: cardId, rarity, mode, cost, wave });
  }

  logReroll(mode, cost, wave) {
    if (!this.session) return;
    this.session.summary.rerolls++;
    this._log('reroll', { mode, cost, wave });
  }

  logShopOpened(origin, wave) {
    if (!this.session) return;
    this._log('shop_opened', { origin, wave });
  }

  logShopClosed(origin, wave) {
    if (!this.session) return;
    this._log('shop_closed', { origin, wave });
  }

  // Player took the rebirth sentinel — same telemetry session continues
  // but wave/deck/stats reset. `waveBefore` lets the analyzer reconstruct
  // the run timeline across the reset boundary.
  logRebirth(waveBefore, score) {
    if (!this.session) return;
    this.session.summary.rebirths = (this.session.summary.rebirths || 0) + 1;
    this._log('rebirth', { waveBefore, score });
  }

  logFlawless(wave, bonus) {
    if (!this.session) return;
    this.session.summary.flawlessWaves++;
    this._log('flawless', { wave, bonus });
  }

  logDeath(wave, finalScore) {
    if (!this.session) return;
    this.session.summary.died = true;
    this.session.summary.finalScore = finalScore;
    this._log('player_died', { wave, finalScore });
  }

  // Inject player name into the active session's meta. Called from the
  // game-over flow once the user types their name, so the upload that
  // immediately follows endSession includes the attribution.
  setPlayerName(name) {
    if (!this.session) return;
    this.session.meta = this.session.meta || {};
    if (name) this.session.meta.playerName = name;
  }

  // Finalize the current session and persist it to localStorage.
  // `extras` lets the caller merge terminal state (final score, etc.)
  // into the summary.
  endSession(extras = {}) {
    if (!this.session) return null;
    this.session.endedAt = nowIso();
    this.session.summary.durationMs = this._nowMs() - this._startMs;
    Object.assign(this.session.summary, extras);

    const finished = this.session;
    this.session = null;

    const sessions = safeRead();
    sessions.push(finished);
    while (sessions.length > MAX_SESSIONS) sessions.shift();
    safeWrite(sessions);

    // Fire-and-forget upload to the telemetry server. Swallows all
    // errors internally; localStorage remains the source of truth.
    uploadSession(finished);

    return finished;
  }

  // Current session snapshot (read-only helper for the HUD / debugging).
  getCurrent() {
    return this.session;
  }

  // --- Export / import ------------------------------------------------

  // Build a downloadable JSON string of all sessions. Pure function —
  // caller is responsible for triggering the browser download.
  buildExportJson() {
    const sessions = safeRead();
    return JSON.stringify(
      {
        exportedAt: nowIso(),
        count: sessions.length,
        sessions,
      },
      null,
      2
    );
  }

  // Trigger a browser download of the exported JSON. No-op in headless
  // environments (tests) where `document` / `Blob` / `URL` are missing.
  downloadExport() {
    if (typeof document === 'undefined' || typeof Blob === 'undefined') return false;
    const json = this.buildExportJson();
    try {
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `korovany-sessions-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      return true;
    } catch (e) {
      return false;
    }
  }
}
